import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import {
  Box, Heading, Spinner, Center, Alert, AlertIcon, HStack, Button, Text,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure, useToast
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import AssessmentList from '../components/assessments/AssessmentList';
import { assessmentService, adminService } from '../services/api';
import { formDataState, assessmentState } from '../state/calculatorState';
import { authState } from '../state/authState';

const safeJsonParse = (value, fallback = {}) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const AssessmentsPage = () => {
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();
  const navigate = useNavigate();
  const setFormData = useSetRecoilState(formDataState);
  const setAssessment = useSetRecoilState(assessmentState);
  const auth = useRecoilValue(authState);
  const isAdmin = ['super_admin', 'manager'].includes(auth.user?.role);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await assessmentService.getAll();
        setAssessments(response.data.assessments);
      } catch (err) {
        setError('Failed to load assessments');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteAssessment = (id) => {
    setAssessments(prev => prev.filter(a => a.id !== id));
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      await assessmentService.deleteAll();
      setAssessments([]);
      toast({
        title: 'All assessments deleted',
        status: 'success',
        duration: 3000
      });
    } catch (err) {
      toast({ title: 'Failed to delete', status: 'error', duration: 3000 });
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  const handleLoadAssessment = (assessment) => {
    const formData = safeJsonParse(assessment.form_data, {});
    setFormData(formData);
    // Track that we're editing this saved assessment
    setAssessment({ id: assessment.id, name: assessment.name });
    navigate('/calculator');
  };

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="red.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mt="15px" mb={4}>
        <Heading size="lg">Assessments</Heading>
        {assessments.length > 0 && (
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            leftIcon={<DeleteIcon />}
            onClick={onOpen}
          >
            Delete All
          </Button>
        )}
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
        {assessments.length > 0 ? (
          <AssessmentList
            assessments={assessments}
            onDelete={handleDeleteAssessment}
            onLoad={handleLoadAssessment}
            showUser={isAdmin}
          />
        ) : (
          <Box textAlign="center" py={10}>
            <Text color="gray.500">No assessments yet. Create one from the Calculator.</Text>
          </Box>
        )}
      </Box>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete All Assessments
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDeleteAll} ml={3} isLoading={isDeleting}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default AssessmentsPage;
